import React, { Component } from 'react';
import isEmpty from 'lodash.isempty';
import LazyLoad from 'react-lazyload';
import loadable from '@loadable/component';
import remove from 'lodash.remove';
import sampleSize from 'lodash.samplesize';
import Select from 'react-select';
import Skeleton from 'react-loading-skeleton';

import { Helmet } from 'react-helmet-async';
import { withRouter } from 'react-router-dom';

// constants
import {
    NEWS_SEARCH_TYPE,
    ALLOWED_QUERY_OPERATOR,
    FILTERED_NEWS_URL_REGEX,
} from '../../utilities/constants';
import { NEWS_POPULAR_CATEGORIES } from '../../utilities/constants/newsCategories';
import { WEB_URL } from '../../utilities/endpoints';

// helpers
import AmplitudeHelperClass from '../../utilities/amplitudeHelper';
import { getNewsSEOData } from '../../utilities/helpers/seoHelpers';

// widgets / shared widgets
import MagnittBanner from '../../micro-frontends/MagnittBanner';
import MagnittLoader from '../../micro-frontends/MagnittLoader';
import SideBannerWidget from '../../micro-frontends/SideBannerWidget';
import NewsletterWidget from '../../micro-frontends/NewsletterWidget';

const StartupList = loadable(() =>
    import(/* webpackChunkName: "StartupList" */ '../../widgets/StartupList'),
);

// reusable components
const ItemRow = loadable(() =>
    import(/* webpackChunkName: "ItemRow" */ '../../reusable-components/ItemRow'),
);
const FeaturedContent = loadable(() =>
    import(/* webpackChunkName: "FeaturedContent" */ '../../reusable-components/FeaturedContent'),
);
const SEOSection = loadable(() =>
    import(/* webpackChunkName: "SEOSection" */ '../../reusable-components/SEOSection'),
);

// APIs
import taxonomyApi from '/src/apis/taxonomyApi';
import newsApi from '/src/apis/newsApi';

// react select styles
import { sharedConstants } from 'shared-helpers';
import { NEWS_DEFAULT_DESCRIPTION, NEWS_DEFAULT_TITLE } from '/src/utilities/constants/messages';
const reactSelectLarge = sharedConstants.reactSelectLarge;
const reactSelectTheme = sharedConstants.reactSelectTheme;

const amplitudeHelper = new AmplitudeHelperClass();

class NewsDirectory extends Component {
    constructor(props) {
        super(props);

        let featuredNewsData;
        if (__isBrowser__) {
            featuredNewsData = window.__INITIAL_DATA__.data;
        } else {
            featuredNewsData = props.staticContext.data;
        }

        this.state = {
            data: [],
            firstApiCall: true,
            searchTitle: '',
            featuredNewsData,

            // navigation
            page: 1,
            limit: 6,

            // search & filters
            searchKeyword: '',
            searchHeader: 'Latest News',
            searchType: NEWS_SEARCH_TYPE.keyword,
            country: '',
            industry: '',
            countries: [],
            industries: [],
            categories: [],
            popularCategories: [],
            filterFields: [
                { name: 'keyword', key: 'keyword', op: ALLOWED_QUERY_OPERATOR.equal, value: [] },
                { name: 'country', key: 'country_id', op: ALLOWED_QUERY_OPERATOR.equal, value: [] },
                { name: 'industry', key: 'industry', op: ALLOWED_QUERY_OPERATOR.equal, value: [] },
                {
                    name: 'category',
                    key: 'news_category',
                    op: ALLOWED_QUERY_OPERATOR.equal,
                    value: [],
                },
            ],
            showMoreLoading: false,
            searchPlaceholder: 'Search',

            // SEO state params
            location_seo: '',
            category_seo: '',
            locations_seo: '',
            categories_seo: '',
            page_title: NEWS_DEFAULT_TITLE,
            page_description: NEWS_DEFAULT_DESCRIPTION,
            page_url: '/news',
            showSEOSection: false,
            clearFilter: false,
        };
    }

    componentDidMount() {

        // use a short search placeholder on mobile
        if (window.innerWidth > 760) {
            this.setState({ searchPlaceholder: 'Search for startup funding news and more' });
        } else {
            this.setState({ searchPlaceholder: 'Search startup funding' });
        }

        // add 4 random catagories to popular catagories array
        const popularCategories = sampleSize(NEWS_POPULAR_CATEGORIES, 4);
        this.setState({ popularCategories });

        const countries = taxonomyApi.getCountries().then((response) => {
            if (response.length > 0) {
                let countries = [];
                response.map((loc) => countries.push({ label: loc.name, value: loc.id }));
                this.setState({ countries });
            }
        });

        const industries = taxonomyApi.getIndustries().then((response) => {
            if (response.length > 0) {
                let industries = [];
                response.map((ind) => industries.push({ label: ind.name, value: ind.id }));
                this.setState({ industries });
            }
        });

        const categories = taxonomyApi.getNewsCategories().then((response) => {
            if (response.length > 0) {
                this.setState({ categories: response });
            }
        });

        const promises = [countries, industries, categories];

        Promise.all(promises).then(() => {
            const url = this.props.match.url;

            /**
             * Check if the url is following this structure /en-:iso_code/news or /en-:iso_code/news/:category
             * then handle the SEO filters and section
             * structure e.g /en-ae/news, /en-ae/news/funding, /en-ae/news/funding?any_string
             */
            if (FILTERED_NEWS_URL_REGEX.test(url)) {
                this.applySEOFilters(url);
            } else {
                this.filterValues();
            }
        });
    }

    getFiltersFromURL = () => {
        var search = this.props.location.search;
        let value = search.substr(search.indexOf('=') + 1);
        let isURLFilter = false;

        if (value) {
            value = decodeURI(value);
            let filterFieldsTemp = this.state.filterFields;

            filterFieldsTemp.map((item) => {
                if (search.indexOf('country') !== -1) {
                    if (item.key === 'country_id') {
                        item.value.push(value);
                        isURLFilter = true;
                    }
                } else if (search.indexOf('industry') !== -1) {
                    if (item.key === 'industry') {
                        item.value.push(value);
                        isURLFilter = true;
                    }
                } else if (search.indexOf('category') !== -1) {
                    if (item.name === 'category') {
                        item.value.push(value);
                        isURLFilter = true;
                    }
                }
            });

            this.setState({ filterFields: filterFieldsTemp });
        }

        return isURLFilter;
    };

    clearFilter() {
        this.setState({ clearFilter: true })
        this.state.popularCategories.map((cat) => cat.selected = false);
        this.setState(
            {
                country: '',
                industry: '',
                searchType: NEWS_SEARCH_TYPE.keyword,
                searchKeyword: '',
                filterFields: [
                    {
                        name: 'keyword',
                        key: 'keyword',
                        op: ALLOWED_QUERY_OPERATOR.equal,
                        value: [],
                    },
                    {
                        name: 'country',
                        key: 'country_id',
                        op: ALLOWED_QUERY_OPERATOR.equal,
                        value: [],
                    },
                    {
                        name: 'industry',
                        key: 'industry',
                        op: ALLOWED_QUERY_OPERATOR.equal,
                        value: [],
                    },
                    {
                        name: 'category',
                        key: 'news_category',
                        op: ALLOWED_QUERY_OPERATOR.equal,
                        value: [],
                    },
                ],
            },
            () => this.filterValues(),
        );
    }

    resetFilters = () => {
        const keyword = this.state.searchKeyword ? [this.state.searchKeyword] : [];
        const categories = this.state.filterFields.find(
            (filter) => filter.name === 'category',
        ).value;

        this.setState({
            country: '',
            industry: '',
            filterFields: [
                {
                    name: 'keyword',
                    key: 'keyword',
                    op: ALLOWED_QUERY_OPERATOR.equal,
                    value: keyword,
                },
                { name: 'country', key: 'country_id', op: ALLOWED_QUERY_OPERATOR.equal, value: [] },
                { name: 'industry', key: 'industry', op: ALLOWED_QUERY_OPERATOR.equal, value: [] },
                {
                    name: 'category',
                    key: 'news_category',
                    op: ALLOWED_QUERY_OPERATOR.equal,
                    value: categories,
                },
            ],
        });
    };

    filterValues = async (isPageChanged = false) => {
        let { limit, page, searchKeyword, industry, country } = this.state;

        let isURLFilter = false;
        if (isEmpty(searchKeyword) && isEmpty(industry) && isEmpty(country) && !this.state.clearFilter) {
            isURLFilter = this.getFiltersFromURL();
        }

        // If page hasn't changed, set it to 1
        // This will help when page was set to > 1 and filters were applied
        if (!isPageChanged) {
            page = 1;
            this.setState({ page });
        }

        newsApi.getNewsList(this.state.filterFields, limit, page).then((response) => {
            const count = response.count;
            const total = response.total;

            let news = response.data;
            if (page > 1) {
                news = this.state.data.concat(response.data);
            }

            const title = this.prepareSearchTitle(count, total);

            // this.resetFilters();
            this.setState(
                {
                    data: news,
                    count: count,
                    searchTitle: title,
                    firstApiCall: false,
                    showMoreLoading: false,
                    total: total,
                },
                () => {
                    if (isURLFilter && document?.getElementById('articles-section')) {
                        document.getElementById('articles-section').scrollIntoView();
                    }
                },
            );
        });
        this.setState({ clearFilter: false })
    };

    /**
     * Method to get the filters from the SEO filtered URL
     * then filter the news list, update the page title and description
     *  and create the SEO section with the news categories and the MENAPT countries
     * @param {String} url   The page URL
     */
    applySEOFilters = async (url = '') => {
        if (!url) {
            this.filterValues();
        }

        // prepare the SEO data
        const seoData = await getNewsSEOData(this.props.match.params, this.state.categories);

        const location = seoData.location_seo;
        const category = seoData.category_seo;

        const tempFilterFields = [...this.state.filterFields];

        // prepare the location and category filters
        tempFilterFields.map((filter) => {
            if (filter.name === 'country' && location) {
                filter.value = [location.id];
            }

            if (filter.name === 'category' && category) {
                filter.value = [category.id];
            }
        });

        this.setState({ page_url: url, filterFields: tempFilterFields, ...seoData }, () =>
            this.filterValues(),
        );
    };

    prepareSearchTitle = (count, total) => {
        const {
            searchType,
            searchKeyword,
            industry,
            country,
            popularCategories,
            location_seo,
            category_seo,
            showSEOSection,
        } = this.state;

        let search = this.props.location.search;
        let searchValue = search.substr(search.indexOf('=') + 1);
        let selectedTags = [];
        // let noFilters = (isEmpty(searchKeyword) && isEmpty(industry) && isEmpty(country));

        // selected a popular category
        popularCategories.map((cat) => {
            if (cat.selected) {
                selectedTags.push(cat.name);
                this.amplitudeTrack('news directory - searched by popular tag');
            }
        });

        // keyword, country, industry search
        if (searchKeyword) {
            selectedTags.push(searchKeyword);
            this.amplitudeTrack('news directory - searched by keyword');
        }

        if (searchType == NEWS_SEARCH_TYPE.country && country) {
            selectedTags.push(country.label);
            this.amplitudeTrack('news directory - searched by country');
        }

        if (searchType == NEWS_SEARCH_TYPE.industry && industry) {
            selectedTags.push(industry.label);
            this.amplitudeTrack('news directory - searched by industry');
        }

        // selected a content category
        if (searchValue) {
            searchValue = decodeURI(searchValue);
            this.state.filterFields.map((x) => {
                if (x.value.length > 0) {
                    let taxonomyToCheck;
                    if (x.name == 'country') {
                        taxonomyToCheck = this.state.countries;
                    } else if (x.name == 'industry') {
                        taxonomyToCheck = this.state.industries;
                    } else if (x.name == 'category') {
                        taxonomyToCheck = this.state.categories;
                    }

                    if (taxonomyToCheck) {
                        taxonomyToCheck.map((y) => {
                            let name = y.name ? y.name : y.label;
                            let id = y.id ? y.id : y.value;
                            if (id == x.value[0]) {
                                selectedTags.push(name);
                                this.amplitudeTrack(
                                    'news directory - searched by ' + x.name + ' tag',
                                );
                            }
                        });
                    }
                }
            });
        }

        if (showSEOSection) {
            if (location_seo) {
                selectedTags.push(location_seo.name);
            }

            if (category_seo) {
                selectedTags.push(category_seo.name);
            }
        }

        // prepare title
        let searchTitle = '';
        let tagsList = '';
        const arr = [...new Set(selectedTags)];
        const resultsString = `Showing ${count} out of ${total} results`;

        if (arr.length > 1) {
            // for multiples, add ',' between each term
            tagsList = arr.slice(0, arr.length - 1).join(', ') + ' and ' + arr.slice(-1);
        } else if (arr.length === 1) {
            tagsList = arr[0];
        }

        if (arr.length === 0) {
            searchTitle = resultsString;
            this.setState({ searchHeader: 'Latest News' });
        } else {
            searchTitle = (
                <>
                    {resultsString} for <span style={{ fontWeight: 'bold' }}>{tagsList}</span>
                </>
            );
            this.setState({ searchHeader: 'Search Results' });
        }

        return searchTitle;
    };

    handleTypeChange = (event) => {
        const type = event.target.name;
        this.resetFilters();
        this.setState({ searchType: type });
        this.amplitudeTrack('clicked ' + type + ' in news dir search');
    };

    handleKeywordChange = (event) => {
        const keyCode = event.keyCode; // looking for 'Enter' key code '13'
        const keyword = this.state.searchKeyword;
        const tempFilterFields = [...this.state.filterFields];

        if (keyCode && keyCode === 13 && keyword) {
            tempFilterFields.map((filter) => {
                if (filter.name == 'keyword') {
                    filter.value = [keyword];
                }
            });

            this.setState({ filterFields: tempFilterFields }, () => this.filterValues());
        }
    };

    handleSelectChange = (option) => {
        const type = this.state.searchType;
        const tempFilterFields = [...this.state.filterFields];

        tempFilterFields.map((filter) => {
            if (filter.name == type) {
                filter.value = [option.value];
            }
        });

        this.setState({ [type]: option, filterFields: tempFilterFields });
    };

    handleSearch = () => {
        const keyword = this.state.searchKeyword;
        const tempFilterFields = [...this.state.filterFields];

        if (keyword) {
            tempFilterFields.map((filter) => {
                if (filter.name == 'keyword') {
                    filter.value = [keyword];
                }
            });
        }

        this.setState({ filterFields: tempFilterFields }, () => this.filterValues());
    };

    handleCategorySelect = (category) => {
        let isSelected = !category.selected;
        const categoryId = category.id;
        const tempFilterFields = [...this.state.filterFields];
        const tempPopularCategories = [...this.state.popularCategories];

        tempPopularCategories.map((cat) => {
            if (cat.id == categoryId) {
                cat.selected = isSelected;
            }
        });

        tempFilterFields.map((filter) => {
            if (filter.name == 'category') {
                if (isSelected) {
                    filter.value.push(categoryId);
                } else {
                    filter.value = remove(filter.value, (id) => categoryId != id);
                }
            }
        });

        this.setState(
            {
                filterFields: tempFilterFields,
                popularCategories: tempPopularCategories,
            },
            () => this.filterValues(),
        );
    };

    handleShowMore = () => {
        this.amplitudeTrack('news dir - clicked show more');
        this.setState({ showMoreLoading: true });
        let { limit, page, total } = this.state;
        const isPageChanged = true;
        page = parseInt(page, 10);

        // Only increment the page if there are more records exists
        if (total > limit * page) {
            page = page + 1;

            this.setState(
                {
                    page: page,
                },
                () => this.filterValues(isPageChanged),
            );
        }
    };

    gtag = (label) => {
        window.gtag('event', 'click', {
            event_category: 'news',
            event_label: label,
        });
    };

    amplitudeTrack = (action) => {
        amplitudeHelper.trackEvent(action);
    };

    render() {
        const { featuredNewsData } = this.state;
        const hasFilter = this.state.filterFields.some((filter) => filter.value.length > 0);
        const featuredNewsDataImage = featuredNewsData?.length > 0 ? featuredNewsData[0]?.image : '';

        return (
            <div className="footer-margin-offset">
                <Helmet>
                    <title>{this.state.page_title}</title>
                    <link rel="canonical" href={`${WEB_URL}${this.state.page_url}`} />
                    <link rel='preload' href={featuredNewsDataImage} as='image' fetchPriority='high' />
                    <meta name="description" content={this.state.page_description} />
                    <meta name="abstract" content={this.state.page_description} />
                    <meta property="og:type" content="article" />
                    <meta property="og:url" content={`${WEB_URL}${this.state.page_url}`} />
                    <meta property="og:title" content={this.state.page_title} />
                    <script type="application/ld+json">
                        {JSON.stringify(
                            {
                                "@context": "https://schema.org",
                                "@type": "BreadcrumbList",
                                "itemListElement": [{
                                    "@type": "ListItem",
                                    "position": 1,
                                    "name": "Startup Ecosystem News from Across Emerging Venture Markets",
                                    "item": `${WEB_URL}/news`
                                }]
                            }
                        )}
                    </script>
                </Helmet>

                <section className="directory content-box content-page">
                    <div className="row content-widgets-row">
                        <div className="widget-banner">
                            <MagnittBanner data={{ match: this.props.match }} />
                        </div>
                        <div className="widget-newsletter responsive-margin-bottom">
                            <NewsletterWidget
                                history={this.props.history}
                                data={{ match: this.props.match, pageType: 'contentDir' }}
                            />
                        </div>
                    </div>

                    {/* featured news */}
                    <div
                        className="db-box mt-0 responsive-margin-bottom featured_news_container"
                        style={{ borderRadius: 5 }}
                    >
                        <h2 className="content-header">Featured News</h2>
                        <FeaturedContent data={featuredNewsData} gtag={() => gtag()} type="news" />
                    </div>

                    {/* search bar */}
                    <div className="row content-search-row">
                        <div className="content-search-box">
                            <div
                                className="db-box mt-0"
                                style={{ borderRadius: 5, height: '100%' }}
                            >
                                <div
                                    className="page-header hero-img"
                                    style={{ textAlign: 'left', maxWidth: 'none' }}
                                >
                                    <div className="row">
                                        <div className="content-search-filters col-12">
                                            <h2 className="content-header">Search</h2>
                                            <div
                                                style={{
                                                    display: 'inline-block',
                                                    marginBottom: 10,
                                                }}
                                            >
                                                <a
                                                    className="selected-search-type"
                                                    data-selected={
                                                        this.state.searchType ==
                                                        NEWS_SEARCH_TYPE.keyword
                                                    }
                                                    name={NEWS_SEARCH_TYPE.keyword}
                                                    onClick={this.handleTypeChange}
                                                >
                                                    keyword
                                                </a>
                                                <a
                                                    className="selected-search-type"
                                                    data-selected={
                                                        this.state.searchType ==
                                                        NEWS_SEARCH_TYPE.country
                                                    }
                                                    name={NEWS_SEARCH_TYPE.country}
                                                    onClick={this.handleTypeChange}
                                                >
                                                    country
                                                </a>
                                                <a
                                                    className="selected-search-type"
                                                    data-selected={
                                                        this.state.searchType ==
                                                        NEWS_SEARCH_TYPE.industry
                                                    }
                                                    name={NEWS_SEARCH_TYPE.industry}
                                                    onClick={this.handleTypeChange}
                                                >
                                                    industry
                                                </a>
                                            </div>
                                            <form
                                                className="search-row m-0"
                                                onSubmit={(e) => e.preventDefault()}
                                            >
                                                {this.state.searchType ==
                                                    NEWS_SEARCH_TYPE.country && (
                                                    <Select
                                                        theme={reactSelectTheme}
                                                        styles={reactSelectLarge}
                                                        placeholder="Country"
                                                        className="col-sm-12 col-md-4 filter-select"
                                                        options={this.state.countries}
                                                        value={this.state.country}
                                                        onChange={this.handleSelectChange}
                                                    />
                                                )}

                                                {this.state.searchType ==
                                                    NEWS_SEARCH_TYPE.industry && (
                                                    <Select
                                                        theme={reactSelectTheme}
                                                        styles={reactSelectLarge}
                                                        placeholder="Industry"
                                                        className="col-sm-12 col-md-4 filter-select"
                                                        options={this.state.industries}
                                                        value={this.state.industry}
                                                        onChange={this.handleSelectChange}
                                                    />
                                                )}

                                                <div className="d-flex col-12 col-md-8 p-0">
                                                    <div className="col-9 form-control clear-outline">
                                                        <div className="input-container">
                                                            <input
                                                                type="text"
                                                                autoComplete="off"
                                                                placeholder={
                                                                    this.state.searchPlaceholder
                                                                }
                                                                value={this.state.searchKeyword}
                                                                onKeyDown={this.handleKeywordChange}
                                                                onChange={(e) =>
                                                                    this.setState({
                                                                        searchKeyword:
                                                                            e.target.value,
                                                                    })
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                    <button
                                                        className="col-3 btn btn-main"
                                                        type="button"
                                                        onClick={this.handleSearch}
                                                        aria-label="Search"
                                                    >
                                                        <i className="fas fa-search"></i>
                                                    </button>
                                                </div>
                                            </form>

                                            <div className="content-tag-box">
                                                {this.state.popularCategories.map(
                                                    (category, index) => (
                                                        <button
                                                            className={
                                                                category.selected
                                                                    ? 'selected-tag border d-inline-block content-tag a-tag-style'
                                                                    : 'border d-inline-block content-tag a-tag-style'
                                                            }
                                                            key={index}
                                                            onClick={() =>
                                                                this.handleCategorySelect(category)
                                                            }
                                                        >
                                                            <div className="d-flex align-items-center">
                                                                <i
                                                                    className={
                                                                        category.selected
                                                                            ? `${category.icon} text-light`
                                                                            : `${category.icon}`
                                                                    }
                                                                    style={{
                                                                        fontSize: 15,
                                                                        marginRight: 8,
                                                                    }}
                                                                ></i>
                                                                <span
                                                                    className={
                                                                        category.selected
                                                                            ? `text-light`
                                                                            : ``
                                                                    }
                                                                    style={{ fontSize: 14 }}
                                                                >
                                                                    {category.name}
                                                                </span>
                                                            </div>
                                                        </button>
                                                    ),
                                                )}
                                            </div>
                                            <div className="research-filters__clear">
                                                <button className="research-filters__clear-anchor a-tag-style" onClick={() => this.clearFilter()} type="button">
                                                    Clear filters
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="content-side-banner">
                            <SideBannerWidget data={{ match: this.props.match }} />
                        </div>
                    </div>

                    {/* latest news / search results */}
                    <div id="articles-section" className="content-block responsive-margin-top">
                        <div className="right-side">
                            <div
                                className="newsContent see-more-list"
                                style={{ borderRadius: 5, background: 'unset', boxShadow: 'unset' }}
                            >
                                <div className="db-box responsive-margin-bottom">
                                    <h2 className="content-header">{this.state.searchHeader}</h2>
                                    {this.state.data.length > 0 ? (
                                        <div className="content-sub-header">
                                            {this.state.searchTitle}
                                        </div>
                                    ) : (
                                        <div className="content-sub-header">
                                            No results were found. Please change your keywords or{' '}
                                            <span
                                                className="red"
                                                onClick={(event) => this.clearFilter(event)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                return to the main listing
                                            </span>
                                            .
                                        </div>
                                    )}
                                </div>

                                {/* result */}
                                {this.state.data === false ? (
                                    <MagnittLoader data={{ height: '300px', background: '#FFF' }} />
                                ) : (
                                    <>
                                        {this.state.data.length > 0 ? (
                                            <div className="row responsive-row">
                                                {this.state.data.map((news, i) => (
                                                    <ItemRow key={i} data={news} keyData={i} />
                                                ))}
                                            </div>)
                                            : hasFilter ? null : (
                                                <div className="row responsive-row">
                                                    {[...Array(6)].map((_, index) => (
                                                        <div key={index} className="col-md-6 responsive-margin-bottom">
                                                            <Skeleton height={224} width={'100%'} />
                                                        </div>
                                                    ))}
                                                </div>
                                            )
                                        }
                                    </>
                                )}

                                {/* show more */}
                                {this.state.count < this.state.total && (
                                    <div className="full-width-show-more">
                                        <span
                                            className="red"
                                            style={{ fontSize: 14.5 }}
                                            onClick={(event) => {
                                                this.handleShowMore();
                                            }}
                                        >
                                            {this.state.showMoreLoading ? (
                                                <i className="fas fa-circle-notch fa-spin mr-2"></i>
                                            ) : (
                                                <i className="fas fa-arrow-circle-down mr-2"></i>
                                            )}
                                            SHOW MORE
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="hidden-md-up">
                            <SideBannerWidget data={{ match: this.props.match }} />
                        </div>

                        <div className="row responsive-margin-top">
                            <div className="col-12 ">
                                <StartupList />
                            </div>
                        </div>
                    </div>

                    {/* SEO Section */}
                    <SEOSection
                        showSEOSection={this.state.showSEOSection}
                        page_description={this.state.page_description}
                        location_seo={this.state.location_seo}
                        category_seo={this.state.category_seo}
                        locations_seo={this.state.locations_seo}
                        categories_seo={this.state.categories_seo}
                    />
                </section>
            </div>
        );
    }
}

export default withRouter(NewsDirectory);


